import React, { useContext, useEffect, useState } from 'react';
import {
    Modal, ModalHeader, ModalBody,
    Form, FormGroup, Label, Input, Button, Row, Col
} from 'reactstrap';
import Select from "react-select";

import { CustoContext } from '../../context/CustoContext';
import { TipoCustoContext } from '../../context/TipoCustoContext';
import { AuthContext } from '../../context/AuthContext';
import { EditCustoType } from '../../context/hooks/useCusto';

interface Props {
    isOpen: boolean;
    toggle: () => void;
    custo: EditCustoType | null;
    selectedContratoId: number;
}

export const EditCustoModal: React.FC<Props> = ({
    isOpen,
    toggle,
    custo,
    selectedContratoId
}) => {
    const { objUserTk } = useContext(AuthContext);
    const { updateCusto, getCustoFinanceiroPaged, getCustoGeralPaged, getCustoAttachment } = useContext(CustoContext);
    const { getAllTipoCustoPaged, listTipoCusto } = useContext(TipoCustoContext);

    const [editingCusto, setEditingCusto] = useState<EditCustoType | null>(null);

    useEffect(() => {
        if (isOpen) {
            getAllTipoCustoPaged(1, 100, objUserTk.EmpresaId);
            if (custo) setEditingCusto(custo);
        }
    }, [isOpen, custo]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, type, checked } = e.target;

        if (!editingCusto) return;

        setEditingCusto(prev => ({
            ...prev!,
            [name]: type === 'checkbox' ? checked : value === '' ? null : value
        }));
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (!editingCusto) return;

        try {
            const updated = await updateCusto(editingCusto);
            if (updated) {
                await getCustoFinanceiroPaged(1, selectedContratoId, objUserTk.EmpresaId, 9);
                await getCustoGeralPaged(1, selectedContratoId, objUserTk.EmpresaId, 9);
                setEditingCusto(null);
                toggle();
            }
        } catch (error) {
            console.error("Erro ao atualizar custo:", error);
        }
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
            setEditingCusto(prev => ({
                ...prev!,
                receiptAttachment: reader.result as string,
            }));
        };
        reader.onerror = (error) => {
            console.error("Erro ao ler arquivo:", error);
        };
    };

    if (!editingCusto) return null;

    const tipoCustoOptions = listTipoCusto.map(tc => ({
        value: tc.id,
        label: tc.description,
    }));

    return (
        <Modal isOpen={isOpen} toggle={toggle} backdrop="static" size="xl">
            <ModalHeader toggle={toggle}>Alterar informações do custo</ModalHeader>
            <ModalBody>
                <Form onSubmit={handleSubmit}>
                    <Row>
                        <Col md="6">
                            <FormGroup>
                                <Label className="form-control-label" htmlFor="input-description">Descrição do custo</Label>
                                <Input
                                    className="form-control-alternative"
                                    type="text"
                                    name="description"
                                    value={editingCusto.description}
                                    onChange={handleChange}
                                />
                            </FormGroup>
                        </Col>
                        <Col md="6">
                            <FormGroup>
                                <Label className="form-control-label" htmlFor="input-description">Tipo de custo</Label>
                                <Select
                                    className="form-control-alternative"
                                    classNamePrefix="react-select"
                                    options={tipoCustoOptions}
                                    placeholder="Selecione ou digite para filtrar..."
                                    isClearable

                                    value={
                                        tipoCustoOptions.find(o => o.value === editingCusto.tipoCustoId) || null
                                    }

                                    onChange={option =>
                                        setEditingCusto(prev =>
                                            prev
                                                ? { ...prev, tipoCustoId: option?.value ?? 0 }
                                                : prev
                                        )
                                    }

                                    filterOption={(opt, input) =>
                                        opt.label.toLowerCase().includes(input.toLowerCase())
                                    }
                                />
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md="2">
                            <FormGroup>
                                <Label className="form-control-label" htmlFor="input-description">Valor individual</Label>
                                <Input
                                    className="form-control-alternative"
                                    type="number"
                                    name="individualValue"
                                    value={editingCusto.individualValue}
                                    onChange={handleChange}
                                />
                            </FormGroup>
                        </Col>
                        <Col md="1">
                            <FormGroup>
                                <Label className="form-control-label" htmlFor="input-description">Saldo</Label>
                                <Input
                                    className="form-control-alternative"
                                    type="number"
                                    name="currentBalance"
                                    value={editingCusto.currentBalance}
                                    onChange={handleChange}
                                />
                            </FormGroup>
                        </Col>
                        <Col md="3">
                            <FormGroup>
                                <Label className="form-control-label" htmlFor="input-description">Data de solicitação</Label>
                                <Input
                                    className="form-control-alternative"
                                    type="date"
                                    name="requestDate"
                                    value={editingCusto.requestDate ?? ''}
                                    onChange={handleChange}
                                />
                            </FormGroup>
                        </Col>
                        <Col md="3">
                            <FormGroup>
                                <Label className="form-control-label" htmlFor="input-description">Data de entrega</Label>
                                <Input
                                    className="form-control-alternative"
                                    type="date"
                                    name="deliveryDate"
                                    value={editingCusto.deliveryDate ?? ''}
                                    onChange={handleChange}
                                />
                            </FormGroup>
                        </Col>
                        <Col md="3">
                            <FormGroup>
                                <Label className="form-control-label" htmlFor="input-description">Data de pagamento</Label>
                                <Input
                                    className="form-control-alternative"
                                    type="date"
                                    name="paymentDate"
                                    value={editingCusto.paymentDate ?? ''}
                                    onChange={handleChange}
                                />
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md="6">
                            <FormGroup>
                                <Label className="form-control-label" htmlFor="input-description">Recebedor</Label>
                                <Input
                                    className="form-control-alternative"
                                    type="text"
                                    name="receiver"
                                    value={editingCusto.receiver}
                                    onChange={handleChange}
                                />
                            </FormGroup>
                        </Col>
                        <Col md="6">
                            <FormGroup>
                                <Label className="form-control-label" htmlFor="input-description">Observação</Label>
                                <Input
                                    className="form-control-alternative"
                                    type="textarea"
                                    name="observation"
                                    value={editingCusto.observation}
                                    onChange={handleChange}
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col md="12">
                            <FormGroup>
                                <Label className="form-control-label" htmlFor="receiptAttachment">
                                    Anexo do Recibo (PDF)
                                </Label>
                                <Input
                                    className="form-control-alternative"
                                    type="file"
                                    name="receiptAttachment"
                                    id="receiptAttachment"
                                    accept=".pdf"
                                    onChange={handleFileChange}
                                />
                                {editingCusto.hasReceiptAttachment && (
                                    <div className="alert alert-success d-flex align-items-center mt-2" role="alert">
                                        <i className="fa-solid fa-file-lines fa-lg mr-2"></i>
                                        <div className="d-flex justify-content-between w-100 align-items-center">
                                            <span className="font-weight-bold">
                                                Um recibo está anexado a esta conta.
                                            </span>
                                            <Button
                                                color="primary"
                                                size="sm"
                                                onClick={() => getCustoAttachment(editingCusto.id)}
                                                className="ml-3"
                                            >
                                                <i className="fa-solid fa-eye mr-1"></i> Visualizar Recibo
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </FormGroup>
                        </Col>
                    </Row>
                    <div className="d-flex justify-content-end mt-3">
                        <Button color="dark" type="submit">Salvar alterações</Button>
                        <Button color="danger" className="ml-2" onClick={toggle}>Cancelar</Button>
                    </div>
                </Form>
            </ModalBody>
        </Modal>
    );
};
